<script context="module">
  const pool = []
</script>

<script>
  import { PUBLIC_MAPBOX_TOKEN } from '$env/static/public'
  import { onMount, onDestroy } from 'svelte'
  import polyline from '@mapbox/polyline'

  import Symbol from '$lib/Symbol.svelte'

  /** @type {[number, number]} */
  const CENTRAL_EUROPE = [16.206412, 50.5890759]

  export let interactive = true

  /** @type {Schema.Location[]} */
  export let locations = []

  /** @type {string[][]} */
  export let polylines = []

  /** @type {import('mapbox-gl').Map} */
  let map
  /** @type {import('mapbox-gl')} */
  let mapboxgl
  let container
  let isLoading = true

  let markers = []

  $: paths = polylines.flatMap(function (lines) {
    return lines.flatMap(function (line) {
      // @ts-ignore Trust decode default precision
      return polyline.decode(line).map((pair) => pair.reverse())
    })
  })

  const fitBounds = () => {
    if (locations.length > 1) {
      const bounds = new mapboxgl.LngLatBounds()

      for (const location of locations) {
        if (location.coordinates?.coordinates.length !== 2) continue
        bounds.extend(
          /** @type {[number, number]} */ (location.coordinates.coordinates)
        )
      }
      map.fitBounds(bounds, {
        animate: true,
        padding: 32
      })
    } else if (locations.length === 1) {
      map.flyTo({
        // @ts-ignore
        center: locations[0].coordinates.coordinates,
        zoom: 5
      })
    } else {
      map.flyTo({
        center: CENTRAL_EUROPE,
        zoom: 5
      })
    }
  }

  const onresize = () => {
    if (!map) return
    fitBounds()
    map.resize()
  }

  const onchange = () => {
    for (const marker of markers) marker.remove()

    markers = locations.map((location) => {
      const element = document.createElement('div')
      element.className = 'marker'
      const marker = new mapboxgl.Marker({ element })
      marker.setLngLat(
        /** @type {[number, number]} */ (location.coordinates.coordinates)
      )
      marker.addTo(map)
      return marker
    })

    fitBounds()

    const source = /** @type {import('mapbox-gl').GeoJSONSource} */ (
      map.getSource('paths')
    )
    if (source) {
      source.setData({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: paths
        }
      })
    }
  }

  const onload = () => {
    map.addSource('paths', {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: []
        }
      }
    })

    map.addLayer({
      id: 'route',
      type: 'line',
      source: 'paths',
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#FC2D54',
        'line-width': 3.5
      }
    })

    onchange()
  }

  const onerror = () => {
    map?.remove()
  }

  onMount(async () => {
    mapboxgl = await import('mapbox-gl')

    if (pool.length) {
      map = pool.pop()
      // eslint-disable-next-line svelte/no-dom-manipulating
      container.replaceWith(map.getContainer())
      isLoading = false
      map.on('error', onerror)
      if (map.loaded()) {
        onchange()
        map.resize()
      } else {
        map.on('load', onload)
      }
    } else {
      map = new mapboxgl.Map({
        container,
        maxZoom: 5,
        interactive: false,
        center: CENTRAL_EUROPE,
        attributionControl: false,
        accessToken: PUBLIC_MAPBOX_TOKEN,
        style: 'mapbox://styles/mapbox/streets-v11'
      })

      if (!map.loaded()) {
        map.on('load', onload)
      } else {
        onload()
      }
    }
  })

  onDestroy(() => {
    for (const marker of markers) marker.remove()
    if (map) {
      pool.push(map)
      const source = /** @type {import('mapbox-gl').GeoJSONSource} */ (
        map.getSource('paths')
      )
      if (source) {
        source.setData({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: []
          }
        })
      }
    }
  })

  $: if ((locations || polylines) && map?.loaded()) onchange()
</script>

<svelte:window on:resize={onresize} />

<svelte:head>
  <link
    href="https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.css"
    rel="stylesheet" />
</svelte:head>

<div class="map" class:interactive class:loading={isLoading}>
  <div class="container" bind:this={container}></div>
  <Symbol name="spinner" />
</div>

<style>
  .map {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 1rem;
    position: absolute;
    top: 0;
    left: 0;
    color: rgb(var(--rgb-muted));
    background: var(--background-alt);
  }

  .container {
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
  }

  .map :global(.mapboxgl-ctrl-logo) {
    display: none !important;
  }

  .map :global(.marker) {
    width: 20px;
    height: 20px;
    background-color: #fff;
    border: 5px solid rgb(var(--rgb-red));
    border-radius: 100%;
    transform: translate(-50%, -50%);
  }

  .map :global(.mapboxgl-popup-close-button) {
    right: 5px !important;
    top: -2px !important;
  }
</style>
